import { useParams, Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { getSitesByCategory } from '@/mocks/sites';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import SchemaOrg from '@/components/base/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-2">
              分类未找到
            </h2>
            <p className="text-sm text-foreground-500 mb-4">请检查分类地址是否正确</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySites = getSitesByCategory(currentCategory.slug);

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: `${currentCategory.name} - 性福宝导航`,
          description: currentCategory.description,
          url: `https://greatkid.cn/category/${currentCategory.slug}`,
        }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory.name },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-100 text-primary-600 rounded-lg">
                <i className={`${currentCategory.icon} text-lg`}></i>
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500">
                  共收录 {categorySites.length} 个站点
                </p>
              </div>
            </div>
            <p className="text-sm text-foreground-600 ml-13">{currentCategory.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900 border border-background-200/70'
                }`}
              >
                {cat.name}
                <span className="ml-1.5 text-xs opacity-70">({cat.count})</span>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categorySites.map((site) => (
              <a
                key={site.id}
                href={site.url}
                target="_blank"
                rel="nofollow noopener"
                className="group p-5 bg-background-50 border border-background-200/70 rounded-xl hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                    {site.name}
                  </h3>
                  <div className="flex items-center gap-1 text-accent-500 flex-shrink-0">
                    <i className="ri-star-fill text-xs"></i>
                    <span className="text-xs font-medium">{site.rating}</span>
                  </div>
                </div>
                <p className="text-xs text-foreground-500 leading-relaxed mb-3 line-clamp-2">
                  {site.description}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {site.tags.map((tag) => (
                    <span
                      key={tag}
                      className="text-[11px] text-secondary-600 bg-secondary-100 px-1.5 py-0.5 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                {site.isNew && (
                  <div className="mt-3">
                    <span className="inline-flex items-center text-[10px] text-primary-500 bg-primary-50 px-1.5 py-0.5 rounded-full">
                      <i className="ri-sparkling-line mr-1"></i>NEW
                    </span>
                  </div>
                )}
              </a>
            ))}
          </div>

          {categorySites.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">该分类下暂无收录站点</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}